/**
 * Staging-host resolution for cross-host URL builders (WEB-776).
 *
 * `NEXT_PUBLIC_LOCI_STAGE` is inlined by Next.js at **build** time. When the
 * Vercel `staging` custom environment omits it, every helper that only reads
 * the env emits production hosts (`app.loci.city`) from a request that
 * `proxy.ts` `classifyHost` already marked `isStaging: true`.
 *
 * Resolution order (first decisive win):
 *   1. explicit `isStaging` (`x-is-staging` from the proxy, via SSR chrome)
 *   2. explicit `host` (Host header / test override)
 *   3. `NEXT_PUBLIC_LOCI_STAGE === 'staging'` (client islands / no host)
 *   4. `window.location.hostname` when running in the browser
 *   5. production
 *
 * Keep this module isomorphic: no `next/headers`, no Node builtins. It is
 * imported by Storybook-bundled client chrome (APO-207).
 */

/** Suffixes whose subdomain layout maps to LOCI surfaces — matches `proxy.ts`. */
const LOCI_HOST_SUFFIXES = ['loci.city', 'loci.localhost'] as const;

export interface CrossHostStageOptions {
  /**
   * Explicit staging flag. `true`/`false` win over env and window. Omit
   * (`undefined`) to fall through to env / request host.
   */
  isStaging?: boolean;
  /** Request `Host` header or `window.location.host` (port stripped). */
  host?: string | null;
}

export function isStagingFromEnv(): boolean {
  return process.env.NEXT_PUBLIC_LOCI_STAGE === 'staging';
}

/**
 * True for the staging apex / app / city hosts that `classifyHost` marks
 * `isStaging: true`. Unknown or non-LOCI hosts return false (same as the
 * proxy fallback branch).
 */
export function isStagingFromHost(rawHost: string | null | undefined): boolean {
  if (!rawHost) return false;
  const host = rawHost.toLowerCase().split(':')[0];
  const suffix = LOCI_HOST_SUFFIXES.find((s) => host === s || host.endsWith(`.${s}`));
  if (!suffix) return false;
  const prefix = host === suffix ? '' : host.slice(0, host.length - suffix.length - 1);
  return prefix === 'staging' || prefix === 'staging.app' || prefix.startsWith('staging-');
}

export function resolveIsStaging(options?: CrossHostStageOptions): boolean {
  if (options?.isStaging === true) return true;
  if (options?.isStaging === false) return false;
  if (options?.host != null && options.host !== '') {
    return isStagingFromHost(options.host);
  }
  if (isStagingFromEnv()) return true;
  if (typeof window !== 'undefined') {
    return isStagingFromHost(window.location.hostname);
  }
  return false;
}
